import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Heart } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, index }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay: 0.25 + index * 0.13, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const HERO_IMG =
  "https://images.unsplash.com/photo-1610992015762-45dca7fa3a85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBuYWlsJTIwYXJ0JTIwbWFuaWN1cmUlMjBsdXh1cnklMjBhZXN0aGV0aWMlMjBzdHVkaW98ZW58MHx8fHwxNzg5NDk1NDU3fDA&ixlib=rb-4.1.0&q=85";

export default function Hero({ onBook }) {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 70]);
  const yCard = useTransform(scrollY, [0, 700], [0, -45]);

  return (
    <section id="inicio" data-testid="hero-section" className="relative overflow-hidden sunset-glow">
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto pt-32 sm:pt-40 pb-16 sm:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[92vh]">
        <div className="max-w-xl">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.3em] uppercase text-gold font-medium"
          >
            <span className="h-px w-8 bg-gold inline-block" />
            Nail Art Studio · Caribe en el corazón
          </motion.p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-obsidian mt-6">
            <MaskedLine index={0}>El arte de</MaskedLine>
            <MaskedLine index={1}>resaltar tu</MaskedLine>
            <MaskedLine index={2}>
              <span className="italic gold-shimmer-text">belleza natural</span>
            </MaskedLine>
          </h1>
          <p className="animate-fade-up delay-2 text-sm sm:text-base text-taupe leading-relaxed mt-6">
            Manicura rusa, soft gel, acrílico esculpido y nail art de autor.
            Cada set se crea con la precisión de una artista y la calidez que
            solo una venezolana sabe poner en los detalles.
          </p>
          <div className="animate-fade-up delay-3 flex flex-wrap items-center gap-4 mt-8">
            <a
              href="#galeria"
              data-testid="hero-explorar-btn"
              className="inline-flex items-center gap-2 rounded-full bg-obsidian hover:bg-cocoa text-white text-sm font-medium px-7 py-3.5 transition-all duration-300 hover:gap-3"
            >
              Explorar Galería
              <ArrowRight className="w-4 h-4" />
            </a>
            <button
              onClick={() => onBook()}
              data-testid="hero-agendar-btn"
              className="inline-flex items-center gap-2 rounded-full border border-gold text-gold hover:bg-gold hover:text-white text-sm font-medium px-7 py-3.5 transition-colors duration-300"
            >
              Agendar Cita
            </button>
          </div>
          <div className="animate-fade-up delay-3 flex items-center gap-6 mt-10 text-xs sm:text-sm text-taupe">
            <span className="flex items-center gap-1.5">
              <Star className="w-4 h-4 text-gold fill-gold" /> 5.0 · Clientas felices
            </span>
            <span className="flex items-center gap-1.5">
              <Heart className="w-4 h-4 text-rosegold fill-rosegold" /> Diseños hechos a mano
            </span>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45, ease: EASE }}
          className="relative"
        >
          <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-rosegold/20 blur-2xl" />
          <div className="absolute -bottom-8 -right-4 w-48 h-48 rounded-full bg-gold/20 blur-2xl" />
          <motion.div
            style={{ y: yImg }}
            className="relative rounded-t-[10rem] rounded-b-3xl overflow-hidden border-4 border-creamcard shadow-2xl shadow-gold/20"
          >
            <img
              src={HERO_IMG}
              alt="Manicura de lujo en ISI'S Nails"
              data-testid="hero-image"
              className="w-full h-[420px] sm:h-[540px] object-cover"
            />
          </motion.div>
          <motion.div style={{ y: yCard }} className="absolute -left-4 sm:-left-10 bottom-10">
            <div className="animate-float-soft backdrop-blur-xl bg-white/85 border border-amber-900/10 shadow-lg rounded-2xl px-5 py-4">
              <p className="font-display text-2xl font-semibold text-obsidian">+300</p>
              <p className="text-xs text-taupe">Sets de autor creados</p>
            </div>
          </motion.div>
          <motion.svg
            animate={{ rotate: 360 }}
            transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
            className="absolute -top-10 -right-4 sm:-right-8 w-28 h-28 sm:w-32 sm:h-32"
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <defs>
              <path id="heroCircle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
            </defs>
            <circle cx="50" cy="50" r="24" className="fill-creamcard/90" />
            <text
              className="fill-gold"
              style={{ fontSize: "8.4px", letterSpacing: "2.6px", fontWeight: 600 }}
            >
              <textPath href="#heroCircle">ISI'S NAILS · ARTE EN TUS MANOS ·</textPath>
            </text>
          </motion.svg>
        </motion.div>
      </div>
    </section>
  );
}
